class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;
    this.bscDrawerUpdateTimer = null;
    this.bscThemeUpdateBound = false;

    this.setHeaderCartIconAccessibility();
    this.bindBscCartUpdates();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  bindBscCartUpdates() {
    window.addEventListener("bsc:cart-update", (event) => {
      const cart = event.detail;
      if (cart && typeof cart.item_count === "number") {
        this.classList.toggle("m-cart--empty", cart.item_count === 0);
        this.updateCartCount(cart.item_count);
      }
      this.scheduleBscDrawerUpdate({ waitForPending: true, delay: 120 });
    });

    const bindThemeUpdate = () => {
      if (
        this.bscThemeUpdateBound ||
        !window.MinimogEvents ||
        !window.MinimogTheme ||
        !window.MinimogTheme.pubSubEvents
      ) {
        return;
      }
      this.bscThemeUpdateBound = true;
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, () => {
        this.scheduleBscDrawerUpdate({ waitForPending: true, delay: 120 });
      });
    };

    bindThemeUpdate();
    document.addEventListener("DOMContentLoaded", bindThemeUpdate);
  }

  getPendingCartMutations() {
    const pending = [];
    if (window.bscCartFetchPromise && typeof window.bscCartFetchPromise.then === "function") {
      pending.push(window.bscCartFetchPromise);
    }
    if (window.bscPendingAdds && window.bscPendingAdds.length) {
      window.bscPendingAdds.forEach((promise) => {
        if (promise && typeof promise.then === "function") pending.push(promise);
      });
    }
    return pending;
  }

  scheduleBscDrawerUpdate(options = {}) {
    clearTimeout(this.bscDrawerUpdateTimer);
    this.bscDrawerUpdateTimer = setTimeout(() => {
      const pending = options.waitForPending ? this.getPendingCartMutations() : [];
      Promise.allSettled(pending).then(() => {
        this.onCartDrawerUpdate(true);
      });
    }, options.delay || 0);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("m-cart-drawer--active");
    this.scheduleBscDrawerUpdate({ waitForPending: true, delay: 0 });
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.open();
    });
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    const sectionUpdate = fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block === "cart-items") {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          } else {
            if (updateFooter) {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
            }
          }
        });
        if (window.bscRenderLiveTokenLabels) window.bscRenderLiveTokenLabels();
      })
      .catch((e) => {
        console.error(e);
      });
    const cartUpdate = this.getCart()
      .then((cart) => {
        this.classList.toggle("m-cart--empty", cart.item_count === 0);
        this.updateCartCount(cart.item_count);
      })
      .catch((e) => {
        console.error(e);
      });
    return Promise.allSettled([sectionUpdate, cartUpdate]);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);
